import { gql } from '@apollo/client';
import { useQuery } from '@tbitop/api';
import { useGraphQLErrorHandler } from '@tbitop/error-handling';
import { usePagination } from '../../core/application/pagination';
import type { AufgabenSeitenweiseFragment, AufgabenSeitenweiseQuery, AufgabenSeitenweiseQueryVariables } from './__generated__/AufgabenSeitenweiseQuery';
import type { AufgabenFilterInput } from './__generated__/globalTypes';
import { AufgabenSortierung as Sortierkriterium, Sortierrichtung } from './__generated__/globalTypes';

export { Sortierkriterium, Sortierrichtung };
export type { AufgabenSeitenweiseFragment as Aufgabe, AufgabenFilterInput as AufgabenFilter };

export const useAufgabenSeitenweiseQuery = (sortierung: Sortierkriterium = Sortierkriterium.Standard, sortierrichtung: Sortierrichtung = Sortierrichtung.Aufsteigend, filter: AufgabenFilterInput) => {
  const { handleWithErrorNotification } = useGraphQLErrorHandler();
  const { paginationVariables, fetchNextPage } = usePagination();

  const { data, loading, error, fetchMore, refetch } = useQuery<AufgabenSeitenweiseQuery, AufgabenSeitenweiseQueryVariables>(gql`
    query AufgabenSeitenweise($anzahlProSeite: Int!, $seite: Int!, $filter: AufgabenFilterInput, $sortierung: AufgabenSortierung!, $sortierrichtung: Sortierrichtung!) {
      aufgabenSeitenweise(seite: $seite, anzahlProSeite: $anzahlProSeite, filter: $filter, sortierung: $sortierung, sortierrichtung: $sortierrichtung) {
        beschreibung {
          gesamtAnzahl
          hatNachfolger
        }
        aktuelleSeite {
          ...AufgabenSeitenweise
        }
      }
    }
    fragment AufgabenSeitenweise on Aufgabe {
      id
      titel
      typ
      status
      ergebnis
      faelligkeit
      kampagne
      objekt {
        id
      }
    }
  `, {
    variables: {
      sortierung,
      sortierrichtung,
      filter,
      ...paginationVariables,
    },
    onError: handleWithErrorNotification('Es konnten keine Aufgaben geladen werden.'),
    notifyOnNetworkStatusChange: true,
  });

  return {
    data: data?.aufgabenSeitenweise?.aktuelleSeite ?? [],
    loading,
    refetch,
    error,
    total: data?.aufgabenSeitenweise?.beschreibung?.gesamtAnzahl,
    pagination: {
      fetchMore: () => fetchNextPage<AufgabenSeitenweiseQuery>(fetchMore)
        .catch(handleWithErrorNotification('Das Laden weiterer Aufgaben ist fehlgeschlagen.')),
      hasMore: data?.aufgabenSeitenweise?.beschreibung?.hatNachfolger ?? false,
    },
  };
};
